import React, { useState, useEffect } from 'react';
import { 
  Sparkles, ThumbsUp, ThumbsDown, Eye, Download, Filter, 
  TrendingUp, AlertCircle, CheckCircle, ChevronDown, ChevronUp,
  Calendar, DollarSign, BarChart3, RefreshCw, GitBranch, Copy,
  MessageSquare, Zap
} from 'lucide-react';
import { 
  PieChart, Pie, Cell, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend 
} from 'recharts';
import { recommendationsAPI, campaignsAPI } from '../services/api';

// Confidence Badge Component
function ConfidenceBadge({ score }) {
  const getColor = () => {
    if (score >= 0.8) return { 
      bg: 'hsl(142, 76%, 96%)', 
      text: 'hsl(142, 48%, 30%)', 
      border: 'hsl(142, 56%, 50%)',
      label: 'High Confidence',
      icon: '🟢'
    };
    if (score >= 0.5) return { 
      bg: 'hsl(38, 92%, 95%)', 
      text: 'hsl(38, 64%, 28%)',
      border: 'hsl(38, 72%, 48%)',
      label: 'Medium Confidence',
      icon: '🟡'
    };
    return { 
      bg: 'hsl(25, 85%, 95%)', 
      text: 'hsl(25, 70%, 35%)',
      border: 'hsl(25, 85%, 55%)',
      label: 'Low Confidence',
      icon: '🔴'
    };
  };
  
  const { bg, text, border, label, icon } = getColor();
  
  return (
    <div style={{ 
      display: 'inline-flex', 
      alignItems: 'center', 
      gap: '8px',
      padding: '8px 16px',
      background: bg,
      border: `2px solid ${border}`,
      color: text,
      borderRadius: '12px',
      fontSize: '0.875rem',
      fontWeight: '700'
    }}>
      <span style={{ fontSize: '1rem' }}>{icon}</span>
      <div>
        <div>{label}</div>
        <div style={{ fontSize: '1.125rem' }}>{(score * 100).toFixed(0)}%</div>
      </div>
    </div>
  );
}

// Chain of Thought Visualization
function ChainOfThoughtViz({ steps, expanded, onToggle }) {
  if (!steps || steps.length === 0) return null;

  return (
    <div style={{ marginTop: '20px' }}>
      <button
        onClick={onToggle}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          background: 'var(--bg-tertiary)',
          border: '1px solid var(--border-medium)',
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: '0.875rem',
          fontWeight: '600',
          color: 'var(--accent-700)'
        }}
        data-testid="toggle-chain-of-thought"
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <GitBranch size={18} />
          Chain of Thought ({steps.length} reasoning steps)
        </div>
        {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      
      {expanded && (
        <div style={{ marginTop: '16px', position: 'relative', paddingLeft: '32px' }}>
          {/* Vertical line */}
          <div style={{
            position: 'absolute',
            left: '15px',
            top: '0',
            bottom: '0',
            width: '3px',
            background: 'linear-gradient(180deg, var(--accent-500), var(--accent-300))',
            borderRadius: '2px'
          }}></div>
          
          {steps.map((step, idx) => (
            <div key={idx} style={{ 
              marginBottom: idx < steps.length - 1 ? '16px' : '0',
              position: 'relative'
            }}>
              {/* Step number badge */}
              <div style={{
                position: 'absolute',
                left: '-32px',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                background: 'var(--accent-500)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.75rem',
                fontWeight: '700',
                border: '3px solid white'
              }}>
                {idx + 1}
              </div>
              
              <div style={{ 
                padding: '12px 16px',
                background: 'white',
                border: '1px solid var(--border-light)',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
              }}>
                <p style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '6px' }}>
                  {step.step}
                </p>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                  {typeof step.details === 'string' ? step.details : JSON.stringify(step.details)}
                </p>
                {step.timestamp && (
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginTop: '6px' }}>
                    {new Date(step.timestamp).toLocaleTimeString()}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Allocation Chart Component
function AllocationChart({ allocations }) {
  if (!allocations) return null;

  const data = Object.entries(allocations).map(([channel, amount]) => ({
    channel: channel.charAt(0).toUpperCase() + channel.slice(1),
    amount: amount,
    percentage: 0
  }));

  const total = data.reduce((sum, item) => sum + item.amount, 0);
  data.forEach(item => {
    item.percentage = ((item.amount / total) * 100).toFixed(1);
  });

  const COLORS = ['#3B82F6', '#14B8A6', '#F59E0B', '#8B5CF6', '#EC4899'];

  return (
    <div className="grid grid-cols-2" style={{ gap: '24px', marginTop: '16px' }}>
      {/* Pie Chart */}
      <div>
        <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '12px', color: 'var(--text-secondary)' }}>
          Budget Distribution
        </h4>
        <div style={{ height: '200px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry) => `${entry.channel} (${entry.percentage}%)`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="amount"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bar Chart */}
      <div>
        <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '12px', color: 'var(--text-secondary)' }}>
          Channel Allocation
        </h4>
        <div style={{ height: '200px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
              <XAxis dataKey="channel" style={{ fontSize: '12px' }} />
              <YAxis style={{ fontSize: '12px' }} />
              <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
              <Bar dataKey="amount" radius={[8, 8, 0, 0]}>
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

// Recommendation Detail Modal
function RecommendationDetailModal({ recommendation, onClose, onFeedback }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [chainExpanded, setChainExpanded] = useState(false);
  const [negotiating, setNegotiating] = useState(false);
  const [budgetConstraint, setBudgetConstraint] = useState(100000);

  const output = recommendation.recommendations?.[0]?.recommendation || {};
  const confidence = recommendation.confidence_score || 0.75;
  const chainOfThought = output.chain_of_thought || { steps: [] };
  const allocations = output.allocation_recommendation?.recommended_allocations || {};

  const handleNegotiate = () => {
    setNegotiating(true);
  };

  const handleApplyConstraint = async () => {
    // In production, would call API to recalculate with new budget
    alert(`Recalculating recommendation with $${budgetConstraint.toLocaleString()} budget constraint...`);
    setNegotiating(false);
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '20px',
          width: '95%',
          maxWidth: '1400px',
          maxHeight: '95vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="recommendation-detail-modal"
      >
        {/* Header */}
        <div style={{ 
          padding: '24px 32px', 
          borderBottom: '2px solid var(--border-light)',
          background: 'linear-gradient(135deg, hsl(210, 100%, 98%) 0%, white 100%)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{ 
                  background: 'var(--accent-500)', 
                  padding: '12px', 
                  borderRadius: '12px',
                  color: 'white'
                }}>
                  <Sparkles size={24} />
                </div>
                <div>
                  <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '4px' }}>
                    AI Budget Recommendation
                  </h2>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    Generated {new Date(recommendation.created_at).toLocaleString()} • 
                    Status: <span style={{ 
                      fontWeight: '600',
                      textTransform: 'capitalize',
                      color: recommendation.status === 'approved' ? 'var(--success-600)' : 
                             recommendation.status === 'pending' ? 'var(--warning-600)' : 'var(--text-primary)'
                    }}>
                      {recommendation.status}
                    </span>
                  </p>
                </div>
              </div>
              <ConfidenceBadge score={confidence} />
            </div>
            
            <button onClick={onClose} className="btn btn-secondary">
              ✕ Close
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ borderBottom: '2px solid var(--border-light)', padding: '0 32px', background: 'white' }}>
          <div style={{ display: 'flex', gap: '4px' }}>
            {[
              { id: 'overview', label: 'Overview', icon: BarChart3 },
              { id: 'allocation', label: 'Allocation Details', icon: DollarSign },
              { id: 'reasoning', label: 'AI Reasoning', icon: GitBranch },
              { id: 'actions', label: 'Actions', icon: Zap }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    padding: '14px 20px',
                    border: 'none',
                    background: activeTab === tab.id ? 'hsl(210, 100%, 98%)' : 'transparent',
                    borderBottom: activeTab === tab.id ? '3px solid var(--primary-500)' : '3px solid transparent',
                    color: activeTab === tab.id ? 'var(--primary-600)' : 'var(--text-secondary)',
                    fontWeight: activeTab === tab.id ? '700' : '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s',
                    fontSize: '0.875rem'
                  }}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon size={16} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '32px' }}>
          {activeTab === 'overview' && (
            <div>
              {/* Key Metrics */}
              <div className="grid grid-cols-4" style={{ gap: '16px', marginBottom: '32px' }}>
                <div style={{ padding: '20px', background: 'var(--bg-secondary)', borderRadius: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <DollarSign size={18} style={{ color: 'var(--primary-500)' }} />
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: '600' }}>
                      Total Budget
                    </p>
                  </div>
                  <p style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--primary-600)' }}>
                    ${Object.values(allocations).reduce((sum, val) => sum + val, 0).toLocaleString()}
                  </p>
                </div>

                <div style={{ padding: '20px', background: 'var(--bg-secondary)', borderRadius: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <TrendingUp size={18} style={{ color: 'var(--success-500)' }} />
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: '600' }}>
                      Expected Return
                    </p>
                  </div>
                  <p style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--success-600)' }}>
                    ${(output.allocation_recommendation?.expected_return || 0).toLocaleString()}
                  </p>
                </div>

                <div style={{ padding: '20px', background: 'var(--bg-secondary)', borderRadius: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <BarChart3 size={18} style={{ color: 'var(--accent-500)' }} />
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: '600' }}>
                      Channels
                    </p>
                  </div>
                  <p style={{ fontSize: '2rem', fontWeight: '700' }}>
                    {Object.keys(allocations).length}
                  </p>
                </div>

                <div style={{ padding: '20px', background: 'var(--bg-secondary)', borderRadius: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <Sparkles size={18} style={{ color: 'var(--accent-500)' }} />
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: '600' }}>
                      Simulations Run
                    </p>
                  </div>
                  <p style={{ fontSize: '2rem', fontWeight: '700' }}>
                    {(output.allocation_recommendation?.simulation_runs || 0).toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Quick Allocation Summary */}
              <div style={{ padding: '24px', background: 'var(--bg-tertiary)', borderRadius: '12px', marginBottom: '24px' }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '16px' }}>
                  Recommended Allocation
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                  {Object.entries(allocations).map(([channel, amount]) => {
                    const percentage = (amount / Object.values(allocations).reduce((sum, val) => sum + val, 0)) * 100;
                    return (
                      <div key={channel} style={{ 
                        padding: '16px', 
                        background: 'white', 
                        borderRadius: '10px',
                        border: '2px solid var(--border-light)',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--primary-500)'}
                      onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border-light)'}
                      >
                        <p style={{ fontSize: '0.875rem', fontWeight: '600', textTransform: 'capitalize', marginBottom: '8px', color: 'var(--text-secondary)' }}>
                          {channel}
                        </p>
                        <p style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--primary-600)', marginBottom: '4px' }}>
                          ${amount.toLocaleString()}
                        </p>
                        <p style={{ fontSize: '0.875rem', color: 'var(--text-tertiary)' }}>
                          {percentage.toFixed(1)}% of budget
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Confidence Interval */}
              {output.allocation_recommendation?.confidence_interval && (
                <div style={{ padding: '20px', background: 'hsl(199, 89%, 96%)', border: '2px solid var(--info-500)', borderRadius: '12px' }}>
                  <h4 style={{ fontSize: '1rem', fontWeight: '700', marginBottom: '12px', color: 'var(--info-700)' }}>
                    95% Confidence Interval
                  </h4>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div>
                      <p style={{ fontSize: '0.75rem', color: 'var(--info-700)', marginBottom: '4px' }}>Lower Bound (5th percentile)</p>
                      <p style={{ fontSize: '1.5rem', fontWeight: '700' }}>
                        ${output.allocation_recommendation.confidence_interval[0]?.toLocaleString()}
                      </p>
                    </div>
                    <div style={{ flex: 1, height: '4px', background: 'var(--info-500)', borderRadius: '2px' }}></div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ fontSize: '0.75rem', color: 'var(--info-700)', marginBottom: '4px' }}>Upper Bound (95th percentile)</p>
                      <p style={{ fontSize: '1.5rem', fontWeight: '700' }}>
                        ${output.allocation_recommendation.confidence_interval[1]?.toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <p style={{ fontSize: '0.875rem', color: 'var(--info-700)', marginTop: '12px', textAlign: 'center' }}>
                    Expected return range based on {output.allocation_recommendation?.simulation_runs || 0} Monte Carlo simulations
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'allocation' && (
            <div>
              <AllocationChart allocations={allocations} />
              
              {/* Allocation Table */}
              <div style={{ marginTop: '32px' }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '16px' }}>
                  Detailed Breakdown
                </h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'var(--bg-secondary)', borderBottom: '2px solid var(--border-medium)' }}>
                        <th style={{ padding: '14px', textAlign: 'left', fontSize: '0.875rem', fontWeight: '700', color: 'var(--text-secondary)' }}>
                          Channel
                        </th>
                        <th style={{ padding: '14px', textAlign: 'right', fontSize: '0.875rem', fontWeight: '700', color: 'var(--text-secondary)' }}>
                          Allocation
                        </th>
                        <th style={{ padding: '14px', textAlign: 'right', fontSize: '0.875rem', fontWeight: '700', color: 'var(--text-secondary)' }}>
                          % of Budget
                        </th>
                        <th style={{ padding: '14px', textAlign: 'right', fontSize: '0.875rem', fontWeight: '700', color: 'var(--text-secondary)' }}>
                          Expected ROI
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(allocations).map(([channel, amount], idx) => {
                        const total = Object.values(allocations).reduce((sum, val) => sum + val, 0);
                        const percentage = (amount / total) * 100;
                        const expectedROI = 150 + (idx * 20); // Simulated ROI
                        
                        return (
                          <tr key={channel} style={{ borderBottom: '1px solid var(--border-light)' }}>
                            <td style={{ padding: '14px', fontWeight: '600', textTransform: 'capitalize' }}>
                              {channel}
                            </td>
                            <td style={{ padding: '14px', textAlign: 'right', fontSize: '1.125rem', fontWeight: '700', color: 'var(--primary-600)' }}>
                              ${amount.toLocaleString()}
                            </td>
                            <td style={{ padding: '14px', textAlign: 'right', fontWeight: '600' }}>
                              {percentage.toFixed(1)}%
                            </td>
                            <td style={{ padding: '14px', textAlign: 'right', fontWeight: '700', color: 'var(--success-600)' }}>
                              {expectedROI}%
                            </td>
                          </tr>
                        );
                      })}
                      <tr style={{ background: 'var(--bg-tertiary)', fontWeight: '700' }}>
                        <td style={{ padding: '14px' }}>Total</td>
                        <td style={{ padding: '14px', textAlign: 'right', fontSize: '1.25rem', color: 'var(--primary-700)' }}>
                          ${Object.values(allocations).reduce((sum, val) => sum + val, 0).toLocaleString()}
                        </td>
                        <td style={{ padding: '14px', textAlign: 'right' }}>100%</td>
                        <td style={{ padding: '14px', textAlign: 'right' }}>-</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'reasoning' && (
            <div>
              {/* Rationale */}
              {output.rationale && (
                <div style={{ 
                  padding: '24px', 
                  background: 'hsl(180, 65%, 97%)', 
                  border: '2px solid var(--accent-500)',
                  borderRadius: '12px',
                  marginBottom: '24px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <MessageSquare size={20} style={{ color: 'var(--accent-700)' }} />
                    <h3 style={{ fontSize: '1rem', fontWeight: '700', color: 'var(--accent-700)' }}>
                      Executive Summary
                    </h3>
                  </div>
                  <p style={{ fontSize: '0.9375rem', color: 'var(--text-primary)', lineHeight: '1.7' }}>
                    {output.rationale}
                  </p>
                </div>
              )}

              {/* Chain of Thought */}
              <ChainOfThoughtViz 
                steps={chainOfThought.steps} 
                expanded={chainExpanded}
                onToggle={() => setChainExpanded(!chainExpanded)}
              />

              {/* Execution Details */}
              {recommendation.execution_log && recommendation.execution_log.length > 0 && (
                <div style={{ marginTop: '24px' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: '700', marginBottom: '12px' }}>
                    Execution Log
                  </h3>
                  <div style={{ padding: '16px', background: 'var(--bg-tertiary)', borderRadius: '8px', maxHeight: '300px', overflowY: 'auto' }}>
                    {recommendation.execution_log.map((log, idx) => (
                      <p key={idx} style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '6px', fontFamily: 'monospace' }}>
                        {log}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'actions' && (
            <div>
              {/* Approval Section */}
              <div style={{ marginBottom: '32px' }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '16px' }}>
                  Decision Actions
                </h3>
                <div className="grid grid-cols-2" style={{ gap: '16px' }}>
                  <div style={{ 
                    padding: '24px', 
                    background: 'hsl(142, 76%, 96%)', 
                    border: '2px solid var(--success-500)',
                    borderRadius: '12px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                      <div style={{ 
                        width: '48px', 
                        height: '48px', 
                        borderRadius: '12px', 
                        background: 'var(--success-500)',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <ThumbsUp size={24} />
                      </div>
                      <div>
                        <h4 style={{ fontSize: '1rem', fontWeight: '700', color: 'var(--success-700)' }}>
                          Approve Recommendation
                        </h4>
                        <p style={{ fontSize: '0.875rem', color: 'var(--success-700)' }}>
                          Accept and implement this allocation
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => onFeedback('accept')}
                      className="btn"
                      style={{ 
                        width: '100%', 
                        justifyContent: 'center', 
                        background: 'var(--success-500)',
                        color: 'white',
                        border: 'none',
                        padding: '14px',
                        fontSize: '0.9375rem',
                        fontWeight: '700'
                      }}
                      data-testid="approve-recommendation"
                    >
                      <ThumbsUp size={18} />
                      Approve Recommendation
                    </button>
                  </div>

                  <div style={{ 
                    padding: '24px', 
                    background: 'hsl(0, 86%, 97%)', 
                    border: '2px solid var(--error-500)',
                    borderRadius: '12px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                      <div style={{ 
                        width: '48px', 
                        height: '48px', 
                        borderRadius: '12px', 
                        background: 'var(--error-500)',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <ThumbsDown size={24} />
                      </div>
                      <div>
                        <h4 style={{ fontSize: '1rem', fontWeight: '700', color: 'var(--error-700)' }}>
                          Override Recommendation
                        </h4>
                        <p style={{ fontSize: '0.875rem', color: 'var(--error-700)' }}>
                          Reject and provide feedback
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => onFeedback('override')}
                      className="btn"
                      style={{ 
                        width: '100%', 
                        justifyContent: 'center', 
                        background: 'var(--error-500)',
                        color: 'white',
                        border: 'none',
                        padding: '14px',
                        fontSize: '0.9375rem',
                        fontWeight: '700'
                      }}
                      data-testid="override-recommendation"
                    >
                      <ThumbsDown size={18} />
                      Override Recommendation
                    </button>
                  </div>
                </div>
              </div>

              {/* Negotiation Section */}
              <div style={{ padding: '24px', background: 'hsl(38, 92%, 95%)', border: '2px solid var(--warning-500)', borderRadius: '12px' }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '12px', color: 'var(--warning-700)' }}>
                  💡 Recommendation Negotiation
                </h3>
                <p style={{ fontSize: '0.875rem', color: 'var(--warning-700)', marginBottom: '16px' }}>
                  Have a different budget constraint? Modify and recalculate.
                </p>
                
                {!negotiating ? (
                  <button
                    onClick={handleNegotiate}
                    className="btn btn-secondary"
                    data-testid="negotiate-btn"
                  >
                    <MessageSquare size={16} />
                    Modify Budget Constraint
                  </button>
                ) : (
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '8px' }}>
                      New Budget Constraint ($)
                    </label>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <input
                        type="number"
                        value={budgetConstraint}
                        onChange={(e) => setBudgetConstraint(parseInt(e.target.value))}
                        min="10000"
                        step="5000"
                        style={{
                          flex: 1,
                          padding: '12px',
                          border: '1px solid var(--border-medium)',
                          borderRadius: '8px',
                          fontSize: '1rem',
                          fontWeight: '600'
                        }}
                      />
                      <button
                        onClick={handleApplyConstraint}
                        className="btn btn-primary"
                      >
                        Recalculate
                      </button>
                      <button
                        onClick={() => setNegotiating(false)}
                        className="btn btn-secondary"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Recommendation Card Component (List View)
function RecommendationCard({ recommendation, onView, onQuickAction, compact = false }) {
  const output = recommendation.recommendations?.[0]?.recommendation || {};
  const confidence = recommendation.confidence_score || 0.75;
  const allocations = output.allocation_recommendation?.recommended_allocations || {};
  const totalBudget = Object.values(allocations).reduce((sum, val) => sum + val, 0);

  const getStatusColor = (status) => {
    const colors = {
      pending: { bg: 'hsl(38, 92%, 95%)', text: 'var(--warning-700)', border: 'var(--warning-500)' },
      approved: { bg: 'hsl(142, 76%, 96%)', text: 'var(--success-700)', border: 'var(--success-500)' },
      accept: { bg: 'hsl(142, 76%, 96%)', text: 'var(--success-700)', border: 'var(--success-500)' },
      override: { bg: 'hsl(0, 86%, 97%)', text: 'var(--error-700)', border: 'var(--error-500)' },
      rejected: { bg: 'hsl(0, 86%, 97%)', text: 'var(--error-700)', border: 'var(--error-500)' }
    };
    return colors[status] || colors.pending;
  };

  const statusColors = getStatusColor(recommendation.status);

  return (
    <div 
      className="card" 
      style={{ 
        marginBottom: '16px',
        border: `2px solid ${statusColors.border}`,
        transition: 'all 0.2s',
        cursor: 'pointer'
      }}
      onClick={() => onView(recommendation)}
      onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
      onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
      data-testid="recommendation-card"
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
          <div style={{ 
            background: 'var(--accent-500)', 
            padding: '10px', 
            borderRadius: '10px',
            color: 'white'
          }}>
            <Sparkles size={20} />
          </div>
          <div>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '4px' }}>
              Budget Optimization Recommendation
            </h3>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              Generated {new Date(recommendation.created_at).toLocaleString()}
            </p>
          </div>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span 
            className="badge"
            style={{ 
              background: statusColors.bg,
              color: statusColors.text,
              border: `1px solid ${statusColors.border}`,
              textTransform: 'capitalize',
              fontSize: '0.875rem',
              padding: '6px 12px'
            }}
          >
            {recommendation.status}
          </span>
          <ConfidenceBadge score={confidence} />
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-4" style={{ gap: '12px', marginBottom: '16px' }}>
        <div style={{ padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px', textAlign: 'center' }}>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>Total Budget</p>
          <p style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--primary-600)' }}>
            ${totalBudget.toLocaleString()}
          </p>
        </div>
        <div style={{ padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px', textAlign: 'center' }}>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>Channels</p>
          <p style={{ fontSize: '1.25rem', fontWeight: '700' }}>
            {Object.keys(allocations).length}
          </p>
        </div>
        <div style={{ padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px', textAlign: 'center' }}>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>Expected Return</p>
          <p style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--success-600)' }}>
            ${(output.allocation_recommendation?.expected_return || 0).toLocaleString()}
          </p>
        </div>
        <div style={{ padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px', textAlign: 'center' }}>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>Agents Used</p>
          <p style={{ fontSize: '1.25rem', fontWeight: '700' }}>
            {recommendation.recommendations?.length || 1}
          </p>
        </div>
      </div>

      {/* Allocation Preview */}
      {!compact && (
        <div style={{ marginBottom: '16px' }}>
          <p style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '10px', color: 'var(--text-secondary)' }}>
            Recommended Allocation
          </p>
          <div style={{ display: 'flex', gap: '8px' }}>
            {Object.entries(allocations).slice(0, 4).map(([channel, amount]) => (
              <div key={channel} style={{ 
                flex: 1,
                padding: '10px 12px', 
                background: 'white', 
                border: '1px solid var(--border-medium)',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <p style={{ fontSize: '0.75rem', textTransform: 'capitalize', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                  {channel}
                </p>
                <p style={{ fontSize: '1rem', fontWeight: '700', color: 'var(--primary-600)' }}>
                  ${(amount / 1000).toFixed(0)}K
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div style={{ 
        display: 'flex', 
        gap: '8px', 
        paddingTop: '16px', 
        borderTop: '1px solid var(--border-light)' 
      }}
      onClick={(e) => e.stopPropagation()}>
        <button
          onClick={() => onView(recommendation)}
          className="btn btn-primary"
          style={{ flex: 1, justifyContent: 'center', fontSize: '0.875rem' }}
        >
          <Eye size={16} />
          View Details
        </button>
        {recommendation.status === 'pending' && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onQuickAction(recommendation._id, 'accept');
              }}
              className="btn"
              style={{ 
                flex: 1, 
                justifyContent: 'center',
                background: 'var(--success-500)',
                color: 'white',
                border: 'none',
                fontSize: '0.875rem'
              }}
            >
              <ThumbsUp size={16} />
              Approve
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onQuickAction(recommendation._id, 'override');
              }}
              className="btn btn-secondary"
              style={{ flex: 1, justifyContent: 'center', fontSize: '0.875rem' }}
            >
              <ThumbsDown size={16} />
              Override
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// Main Recommendations Page
function Recommendations() {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedRecommendation, setSelectedRecommendation] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [filters, setFilters] = useState({
    status: '',
    minConfidence: 0,
    dateRange: 'all'
  });
  const [sortBy, setSortBy] = useState({ field: 'created_at', order: 'desc' });

  useEffect(() => {
    loadRecommendations();
  }, [filters, sortBy]);

  const loadRecommendations = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.status) params.status = filters.status;
      
      const response = await recommendationsAPI.getAll(params);
      let data = response.data.recommendations || [];
      
      // Client-side filtering
      if (filters.minConfidence > 0) {
        data = data.filter(r => (r.confidence_score || 0) >= filters.minConfidence / 100);
      }
      
      // Sorting
      data.sort((a, b) => {
        const aVal = a[sortBy.field] || 0;
        const bVal = b[sortBy.field] || 0;
        return sortBy.order === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
      });
      
      setRecommendations(data);
    } catch (error) {
      console.error('Error loading recommendations:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateRecommendation = async () => {
    try {
      setGenerating(true);
      
      // Get campaigns data
      const campaignsRes = await campaignsAPI.getAll({ limit: 20 });
      const campaigns = campaignsRes.data.campaigns || [];
      
      if (campaigns.length === 0) {
        alert('No campaigns available. Please add campaigns first.');
        return;
      }
      
      // Generate recommendation with real campaign data
      await recommendationsAPI.generate({
        campaigns: campaigns.map(c => ({
          campaign_id: c.campaign_id,
          spend: c.spend || 0,
          conversions: Math.floor(Math.random() * 500) + 50,
          clicks: Math.floor(Math.random() * 5000) + 500,
          impressions: Math.floor(Math.random() * 50000) + 5000
        })),
        budget_total: campaigns.reduce((sum, c) => sum + (c.spend || 0), 0) || 100000,
        creative_assets: [],
        competitors: ['Competitor A', 'Competitor B', 'Competitor C']
      });
      
      // Reload recommendations
      await loadRecommendations();
    } catch (error) {
      console.error('Error generating recommendation:', error);
      alert('Error generating recommendation. Check console for details.');
    } finally {
      setGenerating(false);
    }
  };

  const handleViewDetails = (rec) => {
    setSelectedRecommendation(rec);
    setShowDetailModal(true);
  };

  const handleQuickAction = async (recId, action) => {
    try {
      await recommendationsAPI.submitFeedback({
        recommendation_id: recId,
        feedback_type: action,
        feedback_reason: action === 'accept' ? 'Quick approved' : 'Quick override'
      });
      
      await loadRecommendations();
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  const handleFeedback = async (action) => {
    if (!selectedRecommendation) return;
    
    try {
      await recommendationsAPI.submitFeedback({
        recommendation_id: selectedRecommendation._id,
        feedback_type: action,
        feedback_reason: action === 'accept' ? 'Approved via detail modal' : 'Overridden via detail modal'
      });
      
      setShowDetailModal(false);
      await loadRecommendations();
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  // Calculate summary stats
  const pendingCount = recommendations.filter(r => r.status === 'pending').length;
  const approvedCount = recommendations.filter(r => r.status === 'accept' || r.status === 'approved').length;
  const avgConfidence = recommendations.length > 0 
    ? recommendations.reduce((sum, r) => sum + (r.confidence_score || 0), 0) / recommendations.length 
    : 0;

  return (
    <div className="page-container">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 className="page-title">AI Recommendations</h1>
          <p className="page-subtitle">Multi-agent budget optimization with explainable decisions</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button 
            className="btn btn-secondary"
            onClick={loadRecommendations}
            disabled={loading}
          >
            <RefreshCw size={20} />
            Refresh
          </button>
          <button 
            onClick={generateRecommendation}
            className="btn btn-primary"
            disabled={generating}
            data-testid="generate-recommendation-btn"
            style={{ fontSize: '0.9375rem', padding: '12px 24px', fontWeight: '700' }}
          >
            <Sparkles size={20} />
            {generating ? 'Generating AI Recommendation...' : 'Generate New Recommendation'}
          </button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-4" style={{ gap: '16px', marginBottom: '24px' }}>
        <div className="card">
          <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>
            Total Recommendations
          </p>
          <p style={{ fontSize: '2rem', fontWeight: '700' }}>{recommendations.length}</p>
        </div>
        <div className="card">
          <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>
            Pending Decisions
          </p>
          <p style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--warning-600)' }}>{pendingCount}</p>
        </div>
        <div className="card">
          <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>
            Approved
          </p>
          <p style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--success-600)' }}>{approvedCount}</p>
        </div>
        <div className="card">
          <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>
            Avg Confidence
          </p>
          <p style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--accent-600)' }}>
            {(avgConfidence * 100).toFixed(0)}%
          </p>
        </div>
      </div>

      {/* Filters & Controls */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'end' }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '6px', color: 'var(--text-secondary)' }}>
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--border-light)',
                borderRadius: '8px',
                fontSize: '0.875rem',
                background: 'white'
              }}
              data-testid="filter-status"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="accept">Approved</option>
              <option value="override">Overridden</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <div style={{ minWidth: '200px' }}>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '6px', color: 'var(--text-secondary)' }}>
              Min Confidence
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={filters.minConfidence}
              onChange={(e) => setFilters({ ...filters, minConfidence: parseInt(e.target.value) })}
              style={{ width: '100%' }}
            />
            <p style={{ fontSize: '0.875rem', fontWeight: '600', textAlign: 'center', marginTop: '4px' }}>
              {filters.minConfidence}%
            </p>
          </div>

          <div style={{ minWidth: '180px' }}>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '6px', color: 'var(--text-secondary)' }}>
              Sort By
            </label>
            <select
              value={`${sortBy.field}_${sortBy.order}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('_');
                setSortBy({ field, order });
              }}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--border-light)',
                borderRadius: '8px',
                fontSize: '0.875rem',
                background: 'white'
              }}
            >
              <option value="created_at_desc">Newest First</option>
              <option value="created_at_asc">Oldest First</option>
              <option value="confidence_score_desc">Highest Confidence</option>
              <option value="confidence_score_asc">Lowest Confidence</option>
            </select>
          </div>
        </div>
      </div>

      {/* Recommendations List */}
      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
          <p style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>Loading recommendations...</p>
        </div>
      ) : generating ? (
        <div className="card">
          <div style={{ padding: '60px', textAlign: 'center' }}>
            <div style={{ 
              width: '80px', 
              height: '80px', 
              margin: '0 auto 24px',
              borderRadius: '20px',
              background: 'var(--accent-500)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'pulse 2s ease-in-out infinite'
            }}>
              <Sparkles size={40} style={{ color: 'white' }} />
            </div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '8px' }}>
              Generating AI Recommendation
            </h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>
              Multi-agent orchestration in progress...
            </p>
            <div style={{ maxWidth: '500px', margin: '0 auto' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', textAlign: 'left' }}>
                {['Data Agent: Normalizing campaign data', 
                  'Effectiveness Agent: Calculating efficiency scores',
                  'Scenario Agent: Running Monte Carlo simulations',
                  'Orchestrator: Resolving conflicts and generating recommendation'
                ].map((step, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></div>
                    <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{step}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : recommendations.length === 0 ? (
        <div className="card">
          <div className="empty-state" style={{ padding: '80px 40px' }}>
            <div style={{ 
              width: '120px', 
              height: '120px', 
              margin: '0 auto 24px',
              borderRadius: '30px',
              background: 'linear-gradient(135deg, var(--primary-500), var(--accent-500))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Sparkles size={60} style={{ color: 'white' }} />
            </div>
            <p style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '12px' }}>
              No recommendations yet
            </p>
            <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginBottom: '32px', maxWidth: '500px', margin: '0 auto 32px' }}>
              Click "Generate New Recommendation" to create your first AI-powered budget optimization using multi-agent orchestration
            </p>
            <button 
              onClick={generateRecommendation}
              className="btn btn-primary"
              style={{ fontSize: '1rem', padding: '14px 28px' }}
            >
              <Sparkles size={24} />
              Generate Your First Recommendation
            </button>
          </div>
        </div>
      ) : (
        <div>
          {/* View Mode Toggle */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              Showing {recommendations.length} recommendation{recommendations.length !== 1 ? 's' : ''}
            </p>
          </div>

          {/* Recommendations */}
          <div>
            {recommendations.map((rec) => (
              <RecommendationCard
                key={rec._id}
                recommendation={rec}
                onView={handleViewDetails}
                onQuickAction={handleQuickAction}
                compact={viewMode === 'compact'}
              />
            ))}
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedRecommendation && (
        <RecommendationDetailModal
          recommendation={selectedRecommendation}
          onClose={() => setShowDetailModal(false)}
          onFeedback={handleFeedback}
        />
      )}
    </div>
  );
}

export default Recommendations;
